import {useCallback, useEffect, useState} from "react";

export interface SessionUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface RedirectOptions {
  state?: unknown;
}

function returnTo(options?: RedirectOptions): string {
  const value = options?.state && typeof options.state === "object"
    ? (options.state as {returnTo?: unknown}).returnTo
    : undefined;
  return typeof value === "string" && value.startsWith("/") ? value : "/dashboard";
}

export function useSession() {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    let active = true;
    void fetch("/auth/session", {credentials: "same-origin", cache: "no-store"})
      .then(async (response) => response.ok ? response.json() as Promise<{user: SessionUser}> : {user: null})
      .then(({user: value}) => { if (active) setUser(value); })
      .catch(() => { if (active) setUser(null); })
      .finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, []);

  const signIn = useCallback(async (options?: RedirectOptions) => {
    window.location.assign(`/auth/login?returnTo=${encodeURIComponent(returnTo(options))}`);
  }, []);

  const signUp = useCallback(async (options?: RedirectOptions) => {
    window.location.assign(`/auth/login?screen_hint=sign-up&returnTo=${encodeURIComponent(returnTo(options))}`);
  }, []);

  const signOut = useCallback((options?: {returnTo?: string}) => {
    const destination = options?.returnTo ?? window.location.origin;
    window.location.assign(`/auth/logout?returnTo=${encodeURIComponent(destination)}`);
  }, []);

  return {isLoading, user, signIn, signUp, signOut};
}
